'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

import { prefetchSocialAuthProviders } from '@eliadmin/core/src/components/auth/social-auth-providers-cache';
import Link from '@eliadmin/core/src/components/link';
import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

import { HEADER_BUTTON } from './header-ui';

/** Pages a sign-in should not return to (`?returnUrl=`); also the mobile „Môj účet“ tab. */
export const authPaths = () => [
  ROUTES.SIGN_IN(),
  ROUTES.REGISTER(),
  ROUTES.PASSWORD_RESET(),
  ROUTES.RESET_PASSWORD(),
];

/**
 * Figma header „user“ button: signed in → „Môj účet“ (the profile), a guest → the sign-in page
 * „Môj účet“ (221:13120) that returns here afterwards (`?returnUrl=` with the current path and
 * query; not from the auth pages themselves). Social providers are warmed up on intent, as core.
 */
export function HeaderAccount() {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);

  if (me) {
    return (
      <IconButton asChild icon="user" label={t('text197')} className={HEADER_BUTTON}>
        <Link href={ROUTES.PROFILE() ?? '#'} />
      </IconButton>
    );
  }

  // The query is read under Suspense (static rendering); until then the link carries the path.
  return (
    <Suspense fallback={<GuestLink query="" />}>
      <GuestLinkWithQuery />
    </Suspense>
  );
}

/** Quick access to previous orders; the customer chooses which order to repeat. */
export function HeaderOrders({ mobile = false }: { mobile?: boolean }) {
  const signedIn = useCustomerStore(state => !!state.me);
  if (!signedIn) return null;

  return (
    <IconButton
      asChild
      icon="arrow-clockwise"
      label="Zopakovať objednávku"
      title="Zopakovať objednávku"
      variant={mobile ? 'ghost' : 'outline'}
      size={mobile ? 'lg' : 'md'}
      className={mobile ? 'size-10 rounded-xl' : HEADER_BUTTON}
    >
      <Link href={ROUTES.ORDERS()} prefetch={false} />
    </IconButton>
  );
}

function GuestLinkWithQuery() {
  return <GuestLink query={useSearchParams().toString()} />;
}

function GuestLink({ query }: { query: string }) {
  const { t } = useTranslations();
  const pathname = usePathname();
  const locale = useLocaleStore(state => state.currentLocale);
  const current = query ? `${pathname}?${query}` : pathname;
  const returnUrl = authPaths().some(path => arePathsEqual(path, pathname)) ? undefined : current;
  const warmUp = () => void prefetchSocialAuthProviders(locale);

  return (
    <IconButton
      asChild
      icon="user"
      label={t('text190')}
      className={HEADER_BUTTON}
      onFocus={warmUp}
      onMouseEnter={warmUp}
      onTouchStart={warmUp}
    >
      <Link href={ROUTES.SIGN_IN(returnUrl)} />
    </IconButton>
  );
}
